import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { from, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { SessionService, isRejectedCredential } from '../services/session.service';
import type { UserSession } from '../types/session';
import { UserService } from '../services/user.service';
import { requireSession } from './auth.guard';

export const resubscribeGuard: CanActivateFn = (_route, state) => {
  const router = inject(Router);
  const sessionService = inject(SessionService);
  const userService = inject(UserService);

  return requireSession(state.url).pipe(
    switchMap(canActivate => {
      if (!canActivate) return of(false);

      const cached = sessionService.sessionSnapshot;
      return from(userService.refreshSession()).pipe(
        map(session => decide(session, router)),
        catchError((error: unknown) => {
          // A transient failure must not sign out a valid user; route from the cached session.
          if (cached && !isRejectedCredential(error)) return of(decide(cached, router));
          sessionService.clearSession();
          void router.navigate(['/login', { redirectTo: state.url }]);
          return of(false);
        }),
      );
    }),
  );
};

function decide(session: UserSession, router: Router): boolean {
  const membershipState = session.account.membershipState;

  if (membershipState === 'lapsed_grace') return true;

  if (membershipState === 'free_never_member' || membershipState === 'lapsed_expired') {
    void router.navigate(['getting-started']);
    return false;
  }

  void router.navigate(['budgets']);
  return false;
}
